import { useParams, useNavigate } from 'react-router';
import { useAdminApiQuery } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { ChannelDetailCard } from '@/components/super-admin/channel-detail-card';
import { DetailLoadError } from '@/components/super-admin/detail-load-error';
import { ChevronLeft } from 'lucide-react';
import { RedactedEmail } from '@/components/redacted-email';
import { PageContainer } from '@/components/page-container';
import { userNameOrNull } from '@/lib/user-display-name';

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  createdAt: string;
}

interface Channel {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string;
  metaWabaId: string;
  metaBusinessId: string;
  whatsappWabaName: string;
  whatsappBusinessName: string;
  whatsappPhoneNumberId: string;
  whatsappDisplayPhoneNumber: string;
  maskedToken: string;
  tokenStatus: string;
  tokenExpiresAt: string | null;
  metaConnected: boolean;
  forwardingEnabled: boolean;
  createdAt: string;
  lastRefreshedAt: string | null;
  consecutiveFailures: number;
}

interface UserChannelsResponse {
  user: User;
  channels: Channel[];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function SuperAdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useAdminApiQuery<UserChannelsResponse>(
    qk.superAdmin('users', id, 'channels'),
    `/super-admin/users/${id}/channels`,
  );
  const user = data?.user ?? null;
  const channels = data?.channels ?? [];

  if (isError) {
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to users
        </Button>
        <DetailLoadError
          error={error}
          entity="User"
          backTo="/super-admin/users"
          backLabel="Back To Users"
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" disabled>
          <ChevronLeft className="h-4 w-4" />
          Back to users
        </Button>
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="mt-8 space-y-2">
          <Skeleton className="h-5 w-32" />
        </div>
        <Card>
          <CardContent className="space-y-3 py-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) return null;

  return (
    <PageContainer variant="fluid" className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to users
      </Button>

      <div>
        {/* Email fallback stays masked (RedactedEmail) — never plain text. */}
        <h1 className="text-xl font-semibold">
          {userNameOrNull(user) ?? <RedactedEmail email={user.email} />}
        </h1>
        <p className="flex items-center gap-1 text-sm text-muted-foreground">
          <RedactedEmail email={user.email} /> &middot; Joined {formatDate(user.createdAt)}
        </p>
      </div>

      <h2 className="mt-8 text-base font-semibold">
        Channels ({channels.length})
      </h2>

      {channels.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <h3 className="text-xl font-semibold">No channels connected</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            This user has not connected any WhatsApp Business channels yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {channels.map((channel) => (
            <ChannelDetailCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
